import React, { useState, useEffect, useCallback } from 'react';
// import PropTypes from 'prop-types';
import { Table, Typography, Form, Space, Button, Row, Col } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';

import EditableCell from './EditableCell';
import DragableBodyRow from './DragableBodyRow';
import AddCategory from './AddCategory';

import './style.css';

const ManageCategory = ({
  dataSource,
  onSave,
  onSaveOrder,
  onSaveAddNewCategory,
  columns,
  havingReference,
  reference,
  refData,
}) => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState('');
  const [statusChecked, setStatusChecked] = useState(true);
  const [localDataSource, setLocalDataSource] = useState([]);
  const [isOrderChanged, setIsOrderChanged] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);

  const isEditing = (record) => record.id === editingKey;

  useEffect(() => {
    if (dataSource) {
      setLocalDataSource(dataSource);
    }
  }, [dataSource]);

  const columns2 = [
    ...columns,
    {
      title: 'Action',
      dataIndex: 'action',
      width: '150px',
      align: 'center',
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.id)}
              style={{
                marginRight: 8,
              }}
            >
              Save
            </Typography.Link>
            <Typography.Link onClick={cancel}>Cancel</Typography.Link>
          </span>
        ) : (
          <Typography.Link
            disabled={editingKey !== ''}
            onClick={() => edit(record)}
          >
            Edit
          </Typography.Link>
        );
      },
    },
  ];

  const edit = (record) => {
    form.setFieldsValue({
      name: '',
      code: '',
      ...record,
    });
    setStatusChecked(record.status === 1);
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      onSave(
        {
          ...row,
          status: statusChecked ? 1 : 0,
          id: editingKey,
        },
        (status) => {
          if (status) {
            setEditingKey('');
          }
        }
      );
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const mergedColumns = columns2.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record) => {
        return {
          record,
          inputType:
            col.key === 'status'
              ? 'switch'
              : col.typeReference
              ? 'select'
              : 'text',
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          statusChecked,
          refData,
          setStatusChecked,
        };
      },
    };
  });

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = localDataSource[dragIndex];
      setLocalDataSource(
        update(localDataSource, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        })
      );
      setIsOrderChanged(true);
    },
    [localDataSource]
  );

  const restOrder = () => {
    setLocalDataSource(dataSource);
    setIsOrderChanged(false);
  };

  const onSaveOrderClick = () => {
    const values = localDataSource.map((item) => item.id);
    onSaveOrder(values, () => {
      setIsOrderChanged(false);
    });
  };

  const onSaveAddNewCategoryClicked = (values) => {
    onSaveAddNewCategory(values, () => {
      setShowAddCategoryForm(false);
    });
  };

  return (
    <div style={{ width: '80%', margin: 'auto' }}>
      {!showAddCategoryForm && (
        <Row>
          <Col
            xs={24}
            style={{
              textAlign: 'right',
              marginBottom: '.5rem',
              height: '50px',
              paddingTop: '.5rem',
            }}
          >
            <Space>
              <Button
                onClick={() => {
                  setShowAddCategoryForm(true);
                }}
                type="secondary"
                icon={<PlusOutlined />}
              >
                Add
              </Button>
            </Space>
          </Col>
        </Row>
      )}
      {showAddCategoryForm && (
        <AddCategory
          onCancel={() => setShowAddCategoryForm(false)}
          onSave={onSaveAddNewCategoryClicked}
          havingReference={havingReference}
          refData={refData}
          reference={reference}
        />
      )}
      <Form form={form} component={false}>
        <DndProvider backend={HTML5Backend}>
          <Table
            className="manage-category-grid"
            components={{
              body: {
                row: DragableBodyRow,
                cell: EditableCell,
              },
            }}
            rowKey="index"
            size="small"
            rowClassName={() => 'editable-row'}
            bordered
            dataSource={localDataSource}
            columns={mergedColumns}
            pagination={false}
            onRow={(record, index) => ({
              index,
              moveRow,
            })}
          />
        </DndProvider>
        <Row>
          <Col xs={24} style={{ textAlign: 'right', marginTop: '1rem' }}>
            {isOrderChanged && (
              <Space>
                <Button type="secondary" onClick={restOrder}>
                  Reset Order
                </Button>
                <Button type="primary" onClick={onSaveOrderClick}>
                  Save Order
                </Button>
              </Space>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ManageCategory;
